import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  TextField,
  Grid,
  Paper,
  Button,
  Typography,
  MenuItem,
  InputAdornment,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreatePayee, useUpdatePayee } from '../hooks/useApi';
import { US_STATES } from '../utils/constants';

// Helper functions for formatting
const formatSSN = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
};

const formatEIN = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return numbers;
  return `${numbers.slice(0, 2)}-${numbers.slice(2, 9)}`;
};

const formatPhone = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6)
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(
    6,
    10
  )}`;
};

// Updated Yup validation schema
const schema = yup
  .object({
    firstName: yup.string().when('$payeeType', {
      is: (val: string) => val === 'individual',
      then: (schema) => schema.required('First Name is required'),
      otherwise: (schema) => schema.default(''),
    }),
    lastName: yup.string().when('$payeeType', {
      is: (val: string) => val === 'individual',
      then: (schema) => schema.required('Last Name is required'),
      otherwise: (schema) => schema.default(''),
    }),
    companyName: yup.string().when('$payeeType', {
      is: (val: string) => val === 'business',
      then: (schema) => schema.required('Company Name is required'),
      otherwise: (schema) => schema.default(''),
    }),
    tin: yup.string().when('$payeeType', {
      is: (val: string) => val === 'business',
      then: (schema) => schema.required('EIN is required'),
      otherwise: (schema) => schema.required('SSN is required'),
    }),
    streetAddress: yup.string().required('Street Address is required'),
    address2: yup.string().nullable().notRequired(),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zipCode: yup
      .string()
      .matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP Code')
      .required('ZIP Code is required'),
    phoneNumber: yup.string().nullable().notRequired(),
    email: yup
      .string()
      .email('Invalid email')
      .required('Email Address is required'),
    hourlyRate: yup
      .number()
      .typeError('Hourly Rate must be a number')
      .positive('Hourly Rate must be a positive number')
      .required('Hourly Rate is required'),
  })
  .required();

type FormValues = yup.InferType<typeof schema>;

const mergeRefs =
  (
    hookFormRef: (...args: any[]) => void,
    localRef: React.MutableRefObject<any>
  ) =>
  (el: any) => {
    hookFormRef(el);
    localRef.current = el;
  };

export default function CreatePayee() {
  const location = useLocation();
  const navigate = useNavigate();
  const createMutation = useCreatePayee();
  const updateMutation = useUpdatePayee();
  // Determine if we are editing an existing payee or creating a new one
  const isEditing = location.pathname.includes('editpayee');

  const payee = location.state?.payee || null;
  const payeeType = location.state?.type || payee.payeeType;

  const [paymentType, setPaymentType] = useState<'hourly' | 'flatRate'>(
    payee?.paymentType || 'hourly'
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    trigger,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      firstName: payee?.firstName || '',
      lastName: payee?.lastName || '',
      companyName: payee?.companyName || '',
      tin: payee?.tin || '',
      streetAddress: payee?.streetAddress || '',
      address2: payee?.address2 || '',
      city: payee?.city || '',
      state: payee?.state || '',
      zipCode: payee?.zipCode || '',
      phoneNumber: payee?.phoneNumber || '',
      email: payee?.email || '',
      hourlyRate: payee?.hourlyRate || '',
      // ...add any other fields you use
    },
  });

  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const tinInputRef = useRef<HTMLInputElement | null>(null);

  const { ref: tinRef, onChange: tinOnChange, ...tinRest } = register('tin');
  const {
    ref: phoneRef,
    onChange: phoneOnChange,
    ...phoneRest
  } = register('phoneNumber');

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const employeeData = {
      ...data,
      payeeType,
      paymentType,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      companyName: data.companyName || '',
      tin: data.tin || '',
      streetAddress: data.streetAddress || '',
      city: data.city || '',
      state: data.state || '',
      zipCode: data.zipCode || '',
      email: data.email || '',
      address2: data.address2 || '',
      phoneNumber: data.phoneNumber || '',
    };

    if (isEditing) {
      updateMutation.mutate(
        { id: payee.id, data: employeeData },
        { onSuccess: () => navigate('/payees') }
      );
    } else {
      createMutation.mutate(employeeData, {
        onSuccess: () => navigate('/payees'),
      });
    }
  };

  return (
    <>
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Typography variant="h4" gutterBottom>
          {isEditing ? 'Edit Payee' : 'Create New Payee'}
        </Typography>
        <Paper elevation={3} sx={{ p: 4 }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {payeeType !== 'business' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="First Name"
                      {...register('firstName')}
                      fullWidth
                      size="small"
                      error={!!errors.firstName}
                      helperText={
                        typeof errors.firstName?.message === 'string'
                          ? errors.firstName?.message
                          : undefined
                      }
                      onBlur={() => trigger('firstName')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Last Name"
                      {...register('lastName')}
                      fullWidth
                      size="small"
                      error={!!errors.lastName}
                      helperText={
                        errors.lastName?.message === 'string'
                          ? errors.lastName?.message
                          : undefined
                      }
                      onBlur={() => trigger('lastName')}
                    />
                  </Grid>
                </>
              )}
              {/* Only show Company Name for Business */}
              {payeeType === 'business' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Company Name"
                    {...register('companyName')}
                    fullWidth
                    size="small"
                    error={!!errors.companyName}
                    helperText={
                      errors.companyName?.message === 'string'
                        ? errors.companyName?.message
                        : undefined
                    }
                    onBlur={() => trigger('companyName')}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={payeeType === 'business' ? 'EIN' : 'SSN'}
                  inputRef={mergeRefs(tinRef, tinInputRef)}
                  {...tinRest}
                  value={watch('tin') || ''}
                  onChange={(e) => {
                    const formatted =
                      payeeType === 'business'
                        ? formatEIN(e.target.value)
                        : formatSSN(e.target.value);
                    setValue('tin', formatted);
                    tinOnChange(e);
                  }}
                  fullWidth
                  size="small"
                  error={!!errors.tin}
                  helperText={
                    errors.tin?.message === 'string'
                      ? errors.tin?.message
                      : undefined
                  }
                  onBlur={() => trigger('tin')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Street Address"
                  {...register('streetAddress')}
                  fullWidth
                  size="small"
                  error={!!errors.streetAddress}
                  helperText={
                    errors.streetAddress?.message === 'string'
                      ? errors.streetAddress?.message
                      : undefined
                  }
                  onBlur={() => trigger('streetAddress')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Address 2"
                  {...register('address2')}
                  fullWidth
                  size="small"
                  error={!!errors.address2}
                  helperText={
                    errors.address2?.message === 'string'
                      ? errors.address2?.message
                      : undefined
                  }
                  onBlur={() => trigger('address2')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  {...register('city')}
                  fullWidth
                  size="small"
                  error={!!errors.city}
                  helperText={
                    errors.city?.message === 'string'
                      ? errors.city?.message
                      : undefined
                  }
                  onBlur={() => trigger('city')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="State"
                  fullWidth
                  size="small"
                  {...register('state')}
                  error={!!errors.state}
                  helperText={
                    errors.state?.message === 'string'
                      ? errors.state?.message
                      : undefined
                  }
                  onBlur={() => trigger('state')}
                  // 👇 Ensure a default value is always set
                  SelectProps={{
                    native: false,
                    value: watch('state') ?? '', // fallback to empty string if undefined
                    onChange: (e) => {
                      // let RHF handle the value
                      register('state').onChange(e);
                    },
                  }}
                >
                  {US_STATES.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ZIP Code"
                  {...register('zipCode')}
                  fullWidth
                  size="small"
                  error={!!errors.zipCode}
                  helperText={
                    errors.zipCode?.message === 'string'
                      ? errors.zipCode?.message
                      : undefined
                  }
                  onBlur={() => trigger('zipCode')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  inputRef={mergeRefs(phoneRef, phoneInputRef)}
                  {...phoneRest}
                  value={watch('phoneNumber') || ''}
                  onChange={(e) => {
                    const formatted = formatPhone(e.target.value);
                    setValue('phoneNumber', formatted);
                    phoneOnChange(e);
                  }}
                  fullWidth
                  size="small"
                  autoComplete="tel"
                  error={!!errors.phoneNumber}
                  helperText={
                    errors.phoneNumber?.message === 'string'
                      ? errors.phoneNumber?.message
                      : undefined
                  }
                  onBlur={() => trigger('phoneNumber')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Address"
                  {...register('email')}
                  fullWidth
                  size="small"
                  error={!!errors.email}
                  helperText={
                    errors.email?.message === 'string'
                      ? errors.email?.message
                      : undefined
                  }
                  onBlur={() => trigger('email')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Hourly Rate"
                  {...register('hourlyRate')}
                  fullWidth
                  size="small"
                  type="number"
                  error={!!errors.hourlyRate}
                  helperText={
                    typeof errors.hourlyRate?.message === 'string'
                      ? errors.hourlyRate?.message
                      : paymentType === 'flatRate'
                      ? 'Used for tracking purposes when entering flat rate payments'
                      : undefined
                  }
                  onBlur={() => trigger('hourlyRate')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  placeholder="Hourly Rate"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ mb: 1, fontSize: '0.875rem' }}
                  >
                    Payment Type
                  </FormLabel>
                  <RadioGroup
                    row
                    value={paymentType}
                    onChange={(e) =>
                      setPaymentType(e.target.value as 'hourly' | 'flatRate')
                    }
                  >
                    <FormControlLabel
                      value="hourly"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Hourly
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Hours × Rate = Pay
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="flatRate"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Flat Rate
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Fixed amount per period
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isValid || createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : isEditing
                    ? 'Edit Payee'
                    : 'Create Payee'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </>
  );
}
